import { useMemo } from 'react';

/**
 * The ambient AI layer behind the sign-in hero.
 *
 * Drawn rather than photographed, for three reasons:
 *
 *   1. It carries the brand ramps exactly — every stroke is a token, so the
 *      scene cannot drift from the logo the way a stock photograph would.
 *   2. It scales to any panel size without a crop, and weighs a few kilobytes
 *      instead of a few hundred.
 *   3. It sits *behind* a configurable photograph rather than instead of one:
 *      when the hero image slot is filled from the admin portal, this remains
 *      as the ambience around it.
 *
 * Deliberately restrained. The brief asks for enterprise AI, not science
 * fiction, so this reads as quiet depth — a network settling — rather than a
 * neon grid. Nothing here animates faster than a slow breath, and all of it
 * stops under `prefers-reduced-motion`.
 */

interface Node {
  x: number;
  y: number;
  r: number;
  /** Larger nodes sit in front and carry a little more light. */
  depth: number;
}

interface Edge {
  from: number;
  to: number;
  /** Staggers the pulse so the network does not blink in unison. */
  delay: number;
}

const WIDTH = 720;
const HEIGHT = 900;

/** A fixed generator: the composition is the same on every load and rebuild. */
function rng(seed: number) {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x9e3779b9) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 16), 0x21f0aaad);
    t = Math.imul(t ^ (t >>> 15), 0x735a2d97);
    return ((t ^= t >>> 15) >>> 0) / 4294967296;
  };
}

function build(): { nodes: Node[]; edges: Edge[] } {
  const random = rng(20260924);
  const nodes: Node[] = [];

  // Three loose bands rather than a uniform scatter: a flat field of dots
  // reads as decoration, whereas layered bands read as depth.
  const bands = [
    { count: 16, top: 0.02, bottom: 0.34, depth: 0.45 },
    { count: 18, top: 0.3, bottom: 0.72, depth: 0.75 },
    { count: 14, top: 0.66, bottom: 0.99, depth: 1 },
  ];

  for (const band of bands) {
    for (let index = 0; index < band.count; index += 1) {
      nodes.push({
        x: random() * WIDTH,
        y: (band.top + random() * (band.bottom - band.top)) * HEIGHT,
        r: 1.4 + random() * 2.6 * band.depth,
        depth: band.depth,
      });
    }
  }

  // Join each node to its nearest few neighbours. Nearest-neighbour edges look
  // like a network; random edges look like a scribble.
  const edges: Edge[] = [];
  const seen = new Set<string>();

  nodes.forEach((node, index) => {
    const neighbours = nodes
      .map((other, otherIndex) => ({
        otherIndex,
        distance: Math.hypot(other.x - node.x, other.y - node.y),
      }))
      .filter((entry) => entry.otherIndex !== index)
      .sort((a, b) => a.distance - b.distance)
      .slice(0, 3);

    for (const neighbour of neighbours) {
      if (neighbour.distance > 210) continue;
      const key = [index, neighbour.otherIndex].sort((a, b) => a - b).join(':');
      if (seen.has(key)) continue;
      seen.add(key);
      edges.push({ from: index, to: neighbour.otherIndex, delay: random() * 9 });
    }
  });

  return { nodes, edges };
}

export function AiHeroCanvas() {
  const { nodes, edges } = useMemo(build, []);

  return (
    <svg
      className="aihero__canvas"
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      preserveAspectRatio="xMidYMid slice"
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <radialGradient id="sx-hero-glow-gold" cx="22%" cy="16%" r="62%">
          <stop offset="0%" stopColor="var(--sx-gold-500)" stopOpacity="0.34" />
          <stop offset="100%" stopColor="var(--sx-gold-500)" stopOpacity="0" />
        </radialGradient>
        <radialGradient id="sx-hero-glow-azure" cx="82%" cy="74%" r="66%">
          <stop offset="0%" stopColor="var(--sx-azure-400)" stopOpacity="0.32" />
          <stop offset="100%" stopColor="var(--sx-azure-400)" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="sx-hero-edge" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="var(--sx-azure-300)" stopOpacity="0.55" />
          <stop offset="100%" stopColor="var(--sx-gold-400)" stopOpacity="0.45" />
        </linearGradient>
      </defs>

      {/* Ambient brand light, one warm and one cool, so the panel has depth
          before a single node is drawn. */}
      <rect width={WIDTH} height={HEIGHT} fill="url(#sx-hero-glow-gold)" />
      <rect width={WIDTH} height={HEIGHT} fill="url(#sx-hero-glow-azure)" />

      <g className="aihero__edges">
        {edges.map((edge, index) => {
          const a = nodes[edge.from]!;
          const b = nodes[edge.to]!;
          return (
            <line
              key={index}
              x1={a.x}
              y1={a.y}
              x2={b.x}
              y2={b.y}
              stroke="url(#sx-hero-edge)"
              strokeWidth={0.75}
              style={{ animationDelay: `${edge.delay}s` }}
            />
          );
        })}
      </g>

      <g className="aihero__nodes">
        {nodes.map((node, index) => (
          <circle
            key={index}
            cx={node.x}
            cy={node.y}
            r={node.r}
            fill={index % 5 === 0 ? 'var(--sx-gold-400)' : 'var(--sx-azure-200)'}
            opacity={0.25 + node.depth * 0.5}
            style={{ animationDelay: `${(index % 11) * 0.9}s` }}
          />
        ))}
      </g>
    </svg>
  );
}
